'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { ShowHide, Table, Text, VisuallyHidden } from '@zillow/constellation-10';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { EventType, type HpPriceHistoryResponseDto } from '@zg-rentals/ts-hotpads-api-client';
import { cx } from '@/styled-system/css';
import { FederatedPhoto } from '@/src/components/FederatedPhoto';
import { LucideIcon } from '@/src/components/LucideIcon';
import { emitPriceHistoryEvent } from '@/src/analytics/hotpads/events/priceHistory';
import { Module } from '../ui/Module/Module';
import { usePriceHistoryContext } from './PriceHistoryProvider';
import * as styles from './priceHistory.styles';

const PREVIEW_EVENT_COUNT = 5;

export function PriceHistoryTable({
  history,
  onImpression,
  onExpandedChange,
}: {
  history: HpPriceHistoryResponseDto;
  onImpression?: () => void;
  onExpandedChange?: (expanded: boolean) => void;
}) {
  const t = useTranslations('hdp');
  const locale = useLocale();
  const tableId = useId();
  const [expanded, setExpanded] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const impressed = useRef(false);
  useEffect(() => {
    if (!onImpression || impressed.current || !heading.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || impressed.current) return;
      impressed.current = true;
      onImpression();
      observer.disconnect();
    });
    observer.observe(heading.current);
    return () => observer.disconnect();
  }, [onImpression]);
  const events = expanded ? history.events : history.events.slice(0, PREVIEW_EVENT_COUNT);
  const dateFormat = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  // HDP prices read as US dollars ($2,100) in Spanish too; es-US keeps that while localizing nothing else.
  const numberLocale = locale === 'es' ? 'es-US' : locale;
  const percentFormat = new Intl.NumberFormat(numberLocale, {
    style: 'percent',
    signDisplay: 'exceptZero',
    maximumFractionDigits: 1,
  });
  const mlsIds = [
    ...new Map(
      history.events
        .filter((event) => event.source.mlsListingId)
        .map((event) => [`${event.source.providerName}:${event.source.mlsListingId}`, event.source]),
    ).values(),
  ];

  const table = (
    <Table.Root
      id={tableId}
      aria-label={t('priceHistoryTitle')}
      appearance="horizontal"
      alignments={['start', 'end']}
      size="sm"
      className={styles.table}
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell scope="col">{t('priceHistoryEvent')}</Table.HeaderCell>
          <Table.HeaderCell scope="col">{t('priceHistoryRent')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {events.map((event, index) => {
          const date = event.date ? new Date(event.date) : undefined;
          const money = new Intl.NumberFormat(numberLocale, {
            style: 'currency',
            currency: event.currency,
            maximumFractionDigits: 0,
          });
          const percentChange =
            event.type !== EventType.LISTING_REMOVED && event.percentChange ? event.percentChange : undefined;
          return (
            <Table.Row key={index}>
              <Table.Cell>
                <div className={styles.cellStack}>
                  <Text textStyle="body-bold">{t(`priceHistoryEvents.${event.type}`)}</Text>
                  <span className={styles.meta}>
                    {date && !Number.isNaN(date.getTime()) ? (
                      <time dateTime={event.date}>{dateFormat.format(date)}</time>
                    ) : (
                      <span>{t('priceHistoryDateUnavailable')}</span>
                    )}
                    <span aria-hidden="true">·</span>
                    {event.source.logoUrl && (
                      <FederatedPhoto
                        src={event.source.logoUrl}
                        alt=""
                        className={styles.logo}
                        defaultSize={192}
                        fit="contain"
                        preserveSource
                      />
                    )}
                    <span>{event.source.providerName}</span>
                  </span>
                </div>
              </Table.Cell>
              <Table.Cell>
                <div className={styles.priceStack}>
                  {event.price != null ? (
                    <Text textStyle="body-bold">{money.format(event.price)}</Text>
                  ) : (
                    <Text textStyle="body-bold">
                      <span aria-hidden="true">—</span>
                      <VisuallyHidden>{t('priceHistoryPriceUnavailable')}</VisuallyHidden>
                    </Text>
                  )}
                  {percentChange != null && (
                    <span className={cx(styles.rowDelta, percentChange > 0 ? styles.increase : styles.decrease)}>
                      <LucideIcon icon={percentChange > 0 ? TrendingUp : TrendingDown} size="sm" />
                      <Text textStyle="body-sm-bold" css={{ color: 'inherit' }}>
                        {percentFormat.format(percentChange / 100)}
                      </Text>
                    </span>
                  )}
                </div>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table.Root>
  );

  const footer = (
    <Text asChild textStyle="body-sm" color="text.subtle">
      <p>
        {[
          t('priceHistoryAdvertisedRents'),
          ...mlsIds.map((source) =>
            t('priceHistoryMlsAttribution', { provider: source.providerName, id: source.mlsListingId ?? '' }),
          ),
          history.truncated ? t('priceHistoryTruncated') : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      </p>
    </Text>
  );

  return (
    <Module id="price-history-module" title={t('priceHistoryTitle')} headingRef={heading}>
      <div className={styles.content}>
        {history.events.length > PREVIEW_EVENT_COUNT ? (
          <ShowHide.Root
            appearance="block"
            isHidden={!expanded}
            onDisplayChange={() => {
              setExpanded(!expanded);
              onExpandedChange?.(!expanded);
            }}
            renderButton={() => (
              <ShowHide.Button
                aria-controls={tableId}
                aria-label={t(expanded ? 'priceHistoryShowLess' : 'priceHistoryShowMore')}
              >
                {t(expanded ? 'showLess' : 'showMore')}
              </ShowHide.Button>
            )}
          >
            <div className={styles.content}>
              {table}
              {footer}
            </div>
          </ShowHide.Root>
        ) : (
          <>
            {table}
            {footer}
          </>
        )}
      </div>
    </Module>
  );
}

export function PriceHistoryModule() {
  const { history, identity, listing } = usePriceHistoryContext();
  return history?.events.length ? (
    <PriceHistoryTable
      key={`${identity?.listingAlias}:${identity?.modelId}`}
      history={history}
      onImpression={() => emitPriceHistoryEvent(listing, 'viewed')}
      onExpandedChange={(expanded) => emitPriceHistoryEvent(listing, expanded ? 'expanded' : 'collapsed')}
    />
  ) : null;
}
